import {
  createdAtDayKey,
  createdAtFiveMinKey,
  createdAtHourKey,
  buildFiveMinTrendBuckets,
  buildHourlyTrendBuckets,
  CONNECTION_TREND_DAY_OPTIONS,
  DEFAULT_CONNECTION_TREND_DAYS,
  formatWeekRangeLabel,
  mondayWeekKey,
  ONE_HOUR_TREND_RANGE,
  SIX_HOUR_TREND_RANGE,
  startOfTrendWindow,
  usesWeeklyTrendBuckets,
} from './connectionDashboard';
import { supabase } from './supabase';

export {
  CONNECTION_TREND_DAY_OPTIONS,
  DEFAULT_CONNECTION_TREND_DAYS,
  type ConnectionTrendDayOption as EngagementTrendDayOption,
} from './connectionDashboard';

export type EngagementEventRow = {
  event_type: string;
  occurred_at: string;
  destination_url: string | null;
};

export type EngagementTrendPoint = {
  label: string;
  periodStart: string;
  opens: number;
  clicks: number;
  isWeekStart: boolean;
  isFuture: boolean;
};

export type TopClickLink = {
  url: string;
  label: string;
  clicks: number;
};

export type EngagementDashboardData = {
  scheduledPending: number;
  scheduledNext7Days: number;
  topClicks: TopClickLink[];
  engagementEvents: EngagementEventRow[];
};

function localDayKeyFromDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function startOfLocalDay(d: Date): Date {
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

function formatDayLabel(isoDate: string): string {
  const d = new Date(`${isoDate}T12:00:00`);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function isMonday(isoDate: string): boolean {
  return new Date(`${isoDate}T12:00:00`).getDay() === 1;
}

export function formatClickLinkLabel(url: string): string {
  const trimmed = url.trim() || '(unknown link)';
  try {
    const parsed = new URL(trimmed);
    const path = `${parsed.hostname}${parsed.pathname}`.replace(/\/$/, '');
    const label = path || parsed.hostname;
    return label.length > 42 ? `${label.slice(0, 39)}…` : label;
  } catch {
    return trimmed.length > 42 ? `${trimmed.slice(0, 39)}…` : trimmed;
  }
}

/** Last N calendar days through tomorrow — opens + clicks per day. */
export function buildDailyEngagementTrend(
  events: EngagementEventRow[],
  dayCount: number = DEFAULT_CONNECTION_TREND_DAYS,
): EngagementTrendPoint[] {
  const now = new Date();
  const todayKey = localDayKeyFromDate(startOfLocalDay(now));
  const buckets: {
    key: string;
    label: string;
    opens: number;
    clicks: number;
    isFuture: boolean;
  }[] = [];

  for (let i = dayCount - 1; i >= -1; i--) {
    const d = startOfLocalDay(now);
    d.setDate(d.getDate() - i);
    const key = localDayKeyFromDate(d);
    buckets.push({
      key,
      label: formatDayLabel(key),
      opens: 0,
      clicks: 0,
      isFuture: key > todayKey,
    });
  }

  const bucketIndex = new Map(buckets.map((b, i) => [b.key, i]));

  for (const event of events) {
    const key = createdAtDayKey(event.occurred_at);
    if (!key) continue;
    const idx = bucketIndex.get(key);
    if (idx == null) continue;
    if (event.event_type === 'open') buckets[idx].opens += 1;
    else if (event.event_type === 'click') buckets[idx].clicks += 1;
  }

  return buckets.map((b) => ({
    label: b.label,
    periodStart: b.key,
    opens: b.opens,
    clicks: b.clicks,
    isWeekStart: isMonday(b.key),
    isFuture: b.isFuture,
  }));
}

function buildFiveMinEngagementTrend(events: EngagementEventRow[]): EngagementTrendPoint[] {
  const buckets = buildFiveMinTrendBuckets().map((b) => ({
    ...b,
    opens: 0,
    clicks: 0,
  }));
  const bucketIndex = new Map(buckets.map((b, i) => [b.key, i]));

  for (const event of events) {
    const key = createdAtFiveMinKey(event.occurred_at);
    if (!key) continue;
    const idx = bucketIndex.get(key);
    if (idx == null || buckets[idx].isFuture) continue;
    if (event.event_type === 'open') buckets[idx].opens += 1;
    else if (event.event_type === 'click') buckets[idx].clicks += 1;
  }

  return buckets.map((b) => ({
    label: b.label,
    periodStart: b.key,
    opens: b.opens,
    clicks: b.clicks,
    isWeekStart: b.isDivider,
    isFuture: b.isFuture,
  }));
}

function buildHourlyEngagementTrend(events: EngagementEventRow[], hours: number): EngagementTrendPoint[] {
  const buckets = buildHourlyTrendBuckets(hours).map((b) => ({
    ...b,
    opens: 0,
    clicks: 0,
  }));
  const bucketIndex = new Map(buckets.map((b, i) => [b.key, i]));

  for (const event of events) {
    const key = createdAtHourKey(event.occurred_at);
    if (!key) continue;
    const idx = bucketIndex.get(key);
    if (idx == null || buckets[idx].isFuture) continue;
    if (event.event_type === 'open') buckets[idx].opens += 1;
    else if (event.event_type === 'click') buckets[idx].clicks += 1;
  }

  return buckets.map((b) => ({
    label: b.label,
    periodStart: b.key,
    opens: b.opens,
    clicks: b.clicks,
    isWeekStart: b.isDivider,
    isFuture: b.isFuture,
  }));
}

function aggregateEngagementTrendByWeek(daily: EngagementTrendPoint[]): EngagementTrendPoint[] {
  const weeks = new Map<string, { opens: number; clicks: number }>();

  for (const point of daily) {
    if (point.isFuture) continue;
    const weekKey = mondayWeekKey(point.periodStart);
    const existing = weeks.get(weekKey);
    if (existing) {
      existing.opens += point.opens;
      existing.clicks += point.clicks;
    } else {
      weeks.set(weekKey, { opens: point.opens, clicks: point.clicks });
    }
  }

  return [...weeks.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([weekKey, week]) => ({
      label: formatWeekRangeLabel(weekKey),
      periodStart: weekKey,
      opens: week.opens,
      clicks: week.clicks,
      isWeekStart: true,
      isFuture: false,
    }));
}

/** Short hourly, daily, or weekly buckets depending on range. */
export function buildEngagementTrend(
  events: EngagementEventRow[],
  dayCount: number = DEFAULT_CONNECTION_TREND_DAYS,
): EngagementTrendPoint[] {
  if (dayCount === ONE_HOUR_TREND_RANGE) {
    return buildFiveMinEngagementTrend(events);
  }
  if (dayCount === SIX_HOUR_TREND_RANGE) {
    return buildHourlyEngagementTrend(events, SIX_HOUR_TREND_RANGE);
  }
  const daily = buildDailyEngagementTrend(events, dayCount);
  if (!usesWeeklyTrendBuckets(dayCount)) return daily;
  return aggregateEngagementTrendByWeek(daily);
}

function aggregateTopClicks(events: EngagementEventRow[], limit = 5): TopClickLink[] {
  const windowStart = startOfTrendWindow(7);
  const counts = new Map<string, number>();

  for (const event of events) {
    if (event.event_type !== 'click') continue;
    const day = new Date(`${createdAtDayKey(event.occurred_at)}T12:00:00`);
    if (day < startOfLocalDay(windowStart)) continue;
    const url = event.destination_url?.trim() || '(unknown link)';
    counts.set(url, (counts.get(url) ?? 0) + 1);
  }

  return [...counts.entries()]
    .map(([url, clicks]) => ({ url, label: formatClickLinkLabel(url), clicks }))
    .sort((a, b) => b.clicks - a.clicks)
    .slice(0, limit);
}

export async function fetchEngagementDashboardData(): Promise<EngagementDashboardData> {
  const now = new Date();
  const next7 = new Date(now);
  next7.setDate(next7.getDate() + 7);
  const fetchFrom = startOfTrendWindow(Math.max(...CONNECTION_TREND_DAY_OPTIONS));

  const [pendingResult, next7Result, eventsResult] = await Promise.all([
    supabase
      .from('scheduled_emails')
      .select('id', { count: 'exact', head: true })
      .eq('status', 'pending'),
    supabase
      .from('scheduled_emails')
      .select('id', { count: 'exact', head: true })
      .eq('status', 'pending')
      .gte('scheduled_at', now.toISOString())
      .lte('scheduled_at', next7.toISOString()),
    supabase
      .from('email_engagement_events')
      .select('event_type, occurred_at, destination_url')
      .gte('occurred_at', fetchFrom.toISOString())
      .order('occurred_at', { ascending: false })
      .limit(20000),
  ]);

  if (pendingResult.error) {
    throw new Error(pendingResult.error.message || 'Failed to load scheduled emails');
  }
  if (next7Result.error) {
    throw new Error(next7Result.error.message || 'Failed to load upcoming scheduled emails');
  }
  if (eventsResult.error) {
    throw new Error(eventsResult.error.message || 'Failed to load engagement events');
  }

  const engagementEvents = (eventsResult.data ?? []) as EngagementEventRow[];

  return {
    scheduledPending: pendingResult.count ?? 0,
    scheduledNext7Days: next7Result.count ?? 0,
    topClicks: aggregateTopClicks(engagementEvents),
    engagementEvents,
  };
}
